import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface BillToPartnerOption {
  id: number;
  partnerCode: string;
  legalName: string;
}

interface Props {
  value: BillToPartnerOption | null;
  onChange: (value: BillToPartnerOption | null) => void;
  allowContractingPartyDefault?: boolean;
  disabled?: boolean;
}

const PAGE_SIZE = 20;

function toOption(item: { id: number; partnerCode: string; legalName: string }): BillToPartnerOption {
  return { id: item.id, partnerCode: item.partnerCode, legalName: item.legalName };
}

export function BillToPartnerPicker({
  value,
  onChange,
  allowContractingPartyDefault = false,
  disabled = false,
}: Props) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const query = trpc.customers.list.useQuery(
    { search: debouncedSearch || undefined, page, pageSize: PAGE_SIZE },
    { enabled: true },
  );
  const items = query.data?.items ?? [];
  const totalPages = Math.max(1, query.data?.totalPages ?? 1);
  const start = items.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = (page - 1) * PAGE_SIZE + items.length;

  return (
    <div className="min-w-0 max-w-full space-y-2 rounded-md border p-3 [overflow-wrap:anywhere]">
      <Label htmlFor="bill-to-partner-search">Facturar a</Label>
      {value ? (
        <p className="min-w-0 text-sm font-medium [overflow-wrap:anywhere]">
          Seleccionada: [{value.partnerCode}] {value.legalName}
        </p>
      ) : allowContractingPartyDefault ? (
        <p className="text-sm text-muted-foreground">
          Predeterminado: igual que la Parte contratante. Puedes elegir otra entidad.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">No hay una entidad a facturar asignada.</p>
      )}
      <p className="text-xs text-muted-foreground">
        El cambio afecta solo a quién se factura; no cambia la Parte contratante.
      </p>
      <Input
        id="bill-to-partner-search"
        aria-label="Buscar Facturar a por nombre, NIF o código de cliente"
        placeholder="Buscar por nombre, NIF o código de cliente"
        value={search}
        disabled={disabled}
        onChange={event => setSearch(event.target.value)}
      />
      {query.isLoading ? (
        <p role="status" className="text-sm text-muted-foreground">Cargando Business Partners…</p>
      ) : query.isError ? (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-destructive">No se pudieron cargar las entidades a facturar.</p>
          <Button type="button" variant="outline" disabled={disabled} onClick={() => void query.refetch()}>
            Reintentar búsqueda
          </Button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay Business Partners activos con rol de cliente que coincidan.</p>
      ) : (
        <div className="max-h-56 min-w-0 space-y-1 overflow-y-auto" aria-label="Resultados de Business Partners">
          {items.map(item => {
            const option = toOption(item);
            const label = `[${option.partnerCode}] ${option.legalName}`;
            return (
              <Button
                key={option.id}
                type="button"
                variant="outline"
                aria-pressed={value?.id === option.id}
                aria-label={`Seleccionar Facturar a ${label}`}
                disabled={disabled}
                className="h-auto min-h-11 w-full min-w-0 justify-start whitespace-normal break-words px-3 py-2 text-left"
                onClick={() => onChange(option)}
              >
                {label}
              </Button>
            );
          })}
        </div>
      )}
      {!query.isError && (query.data?.total ?? 0) > 0 && (
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>Resultados {start}–{end} de {query.data?.total}</span>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" disabled={disabled || page <= 1} onClick={() => setPage(current => Math.max(1, current - 1))}>
              Página anterior
            </Button>
            <span className="self-center">Página {page} de {totalPages}</span>
            <Button type="button" variant="outline" size="sm" disabled={disabled || page >= totalPages} onClick={() => setPage(current => Math.min(totalPages, current + 1))}>
              Página siguiente
            </Button>
          </div>
        </div>
      )}
      {allowContractingPartyDefault && value && (
        <Button
          type="button"
          variant="ghost"
          disabled={disabled}
          className="h-auto min-h-11 whitespace-normal text-left"
          onClick={() => onChange(null)}
        >
          Mantener igual que la Parte contratante
        </Button>
      )}
    </div>
  );
}
